/**
 * Captura o frame ATUAL de um `<video>` como JPEG (readback nativo via canvas).
 *
 * <p>Só vale onde o readback de `<video>` funciona (Android/browser/desktop). Em Tizen/webOS
 * o frame vive num overlay de HW e o canvas volta preto — quem chama já filtra por
 * plataforma (`isVideoReadbackAllowedForPlatform`).</p>
 *
 * <p>Resolve `null` quando não há frame (vídeo sem dimensão), quando o canvas está "tainted"
 * (cross-origin sem CORS → SecurityError) ou quando o encoder falha. NUNCA lança: a captura é
 * cosmética e não pode derrubar o avanço do playback.</p>
 */
export function captureVideoFrameJpeg(
  video: HTMLVideoElement,
  quality = 0.9,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      if (!(video.videoWidth > 0) || !(video.videoHeight > 0)) {
        resolve(null);
        return;
      }
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      const cx = c.getContext('2d');
      if (!cx) {
        resolve(null);
        return;
      }
      cx.drawImage(video, 0, 0, c.width, c.height);
      c.toBlob((jpeg) => resolve(jpeg ?? null), 'image/jpeg', quality);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Desenha o frame ATUAL de um `<video>` num canvas (síncrono, sem encode). Usado pelo cover
 * transitório do hop do pulso: o frame que ESTÁ na tela no instante do corte.
 *
 * @returns `true` se desenhou.
 */
export function drawVideoFrameToCanvas(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): boolean {
  try {
    if (!(video.videoWidth > 0) || !(video.videoHeight > 0)) return false;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const cx = canvas.getContext('2d');
    if (!cx) return false;
    cx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return true;
  } catch {
    return false;
  }
}
